export interface RouteStep {
  instruction: string
  distance: number
  duration: number
  modifier?: string
  type?: string
}

export interface RouteData {
  coordinates: [number, number][] // [lat, lng] for Leaflet
  distance: number // meters
  duration: number // seconds
  steps: RouteStep[]
}

export interface RouteError {
  error: string
}

function parseInstruction(
  type: string,
  modifier: string,
  name: string,
): string {
  if (type === 'turn') {
    if (
      modifier === 'left' ||
      modifier === 'sharp left' ||
      modifier === 'slight left'
    )
      return 'Gira a la izquierda'
    if (
      modifier === 'right' ||
      modifier === 'sharp right' ||
      modifier === 'slight right'
    )
      return 'Gira a la derecha'
    if (modifier === 'uturn') return 'Haz un giro en U'
    return 'Gira'
  }
  if (type === 'new name') return 'Continúa por ' + (name || 'la calle actual')
  if (type === 'depart') return 'Dirígete hacia ' + (modifier || 'tu destino')
  if (type === 'arrive') return 'Llegaste a tu destino'
  if (type === 'roundabout') return 'En la rotonda, toma la salida'
  if (type === 'merge') return 'Incorpórate'
  if (type === 'on ramp') return 'Toma la rampa'
  if (type === 'off ramp') return 'Toma la salida'
  if (type === 'fork') {
    if (modifier?.includes('left')) return 'Mantente a la izquierda'
    if (modifier?.includes('right')) return 'Mantente a la derecha'
    return 'Bifurcación'
  }
  if (type === 'end of road') {
    if (modifier?.includes('left'))
      return 'Gira a la izquierda al final de la calle'
    if (modifier?.includes('right'))
      return 'Gira a la derecha al final de la calle'
    return 'Al final de la calle'
  }

  if (modifier === 'straight') return 'Sigue recto'

  return 'Sigue recto'
}

export async function fetchRoute(
  originLat: number,
  originLng: number,
  destLat: number,
  destLng: number,
): Promise<RouteData | RouteError | null> {
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${originLng},${originLat};${destLng},${destLat}?overview=full&geometries=geojson&steps=true`
    const res = await fetch(url)
    if (!res.ok) {
      return { error: 'No se pudo conectar al servicio de rutas' }
    }
    const data = await res.json()

    if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
      return { error: 'No se encontró una ruta disponible' }
    }

    const route = data.routes[0]

    // Convert GeoJSON [lng, lat] to Leaflet [lat, lng]
    const coordinates = route.geometry.coordinates.map(
      (coord: [number, number]) => [coord[1], coord[0]] as [number, number],
    )

    const steps: RouteStep[] = []
    if (route.legs && route.legs.length > 0 && route.legs[0].steps) {
      for (const step of route.legs[0].steps) {
        const maneuver = step.maneuver
        steps.push({
          instruction: parseInstruction(
            maneuver.type,
            maneuver.modifier,
            step.name,
          ),
          distance: step.distance,
          duration: step.duration,
          type: maneuver.type,
          modifier: maneuver.modifier,
        })
      }
    }

    return {
      coordinates,
      distance: route.distance,
      duration: route.duration,
      steps,
    }
  } catch (error) {
    console.error('Error fetching route:', error)
    return { error: 'Error de conexión al calcular la ruta' }
  }
}
